import { FullWidthLayout } from '@/layouts'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import * as Styled from './legalPolicies.stlye'
import { Typography, useTheme } from '@cellectenergy/cellect-react-components'
import { styled } from '@mui/material/styles'

const CustomTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.corporateColors.primary.white,
}))

const PolicyLink = styled('a')(({ theme }) => ({
  color: theme.palette.common.corporateColors.primary.white,
  display: 'inline',
  font: 'inherit',
  textDecoration: 'underline',
  textUnderlineOffset: '0.125rem',
}))

const tableCellSx = {
  borderColor: 'rgba(255, 255, 255, 0.24)',
  verticalAlign: 'top',
}

const cookiesRows = [
  {
    cookie: 'cookiesConsent',
    cookieType: 'Technical/preference',
    ownership: 'Cellect Energy',
    definition: "Stores the user's decision to accept or reject cookies",
    conservationPeriod: '1 year',
  },
  {
    cookie: '_ga',
    cookieType: 'Analytics',
    ownership: 'Google',
    definition: 'Collects user information in order to provide analytics of the website',
    conservationPeriod: '2 years',
  },
  {
    cookie: '_ga_M8DQYEJ7Z9',
    cookieType: 'Analytics',
    ownership: 'Google',
    definition: 'Collects user information in order to provide analytics of the website',
    conservationPeriod: '2 years',
  },
  {
    cookie: '_gat_UA-235355050-1',
    cookieType: 'Analytics',
    ownership: 'Google',
    definition: 'Provides technical monitoring',
    conservationPeriod: '1 minute',
  },
  {
    cookie: '_gcl_au',
    cookieType: 'Analytics',
    ownership: 'Google',
    definition: 'Stores and tracks conversions',
    conservationPeriod: '2 months',
  },
  {
    cookie: '_gid',
    cookieType: 'Analytics',
    ownership: 'Google',
    definition: 'Stores and counts pageviews',
    conservationPeriod: '1 day',
  },
  {
    cookie: 'NID',
    cookieType: 'Analytics',
    ownership: 'Google',
    definition: 'Provides ad delivery or retargeting, stores user preferences',
    conservationPeriod: '6 months',
  },
  {
    cookie: 'DSID',
    cookieType: 'Analytics',
    ownership: 'Google',
    definition: 'Stores users preferences',
    conservationPeriod: '2 weeks',
  },
  {
    cookie: '_GRECAPTCHA',
    cookieType: 'Tecnical',
    ownership: 'Google',
    definition: 'Anti-bot risk analysis and abuse prevention in forms.',
    conservationPeriod: '6 months',
  },
  {
    cookie: '_hjSessionUser_5231672',
    cookieType: 'Analytics',
    ownership: 'Hotjar',
    definition: 'Persists the Hotjar user ID for the site.',
    conservationPeriod: '1 year',
  },
  {
    cookie: '_hjSession_5231672',
    cookieType: 'Analytics',
    ownership: 'Hotjar',
    definition: 'Maintains current session data and attributes requests to the same session.',
    conservationPeriod: '30 minutes',
  },
  {
    cookie: '_hjCookieTest',
    cookieType: 'Analytics',
    ownership: 'Hotjar',
    definition: 'Checks whether Hotjar can use cookies.',
    conservationPeriod: 'Deleted almost immediately after verification',
  },
  {
    cookie: '_hjLocalStorageTest',
    cookieType: 'Analytics',
    ownership: 'Hotjar',
    definition: 'Checks whether Hotjar can use localStorage.',
    conservationPeriod: 'Deleted almost immediately after verification',
  },
  {
    cookie: '_hjSessionStorageTest',
    cookieType: 'Analytics',
    ownership: 'Hotjar',
    definition: 'Checks whether Hotjar can use sessionStorage.',
    conservationPeriod: 'Deleted almost immediately after verification',
  },
]

export const CookiesScreen = () => {
  const theme = useTheme()

  return (
    <FullWidthLayout
      id="free-trial"
      bgColor={theme.palette.common.corporateColors.primary.midNightBlue}
      borderColor="var(--on-primary-dark)"
    >
      <Styled.LegalContainer>
        <Styled.BoxContent>
          <CustomTypography variant="headline-medium" fontWeight={500}>
            Cookies Policy
          </CustomTypography>
        </Styled.BoxContent>

        <Styled.BoxContent>
          <CustomTypography variant="body-medium">
            CELLECT ENERGY SL, (hereinafter &quot;Cellect&quot;) is the owner of the Web Site{' '}
            <PolicyLink href="https://cellectenergy.com/">https://cellectenergy.com/</PolicyLink> (hereinafter the
            &quot;Web Site&quot;) and uses cookies that collect information related to the connection, browsers and
            devices used by Internet users who access and use the Web Site (hereinafter the &quot;Users&quot;). Cellect
            uses this information to manage and improve the proper functioning of the Website.
          </CustomTypography>
          <CustomTypography variant="body-medium">
            This policy describes what information these cookies collect, how they are used and for what purpose. It
            also indicates how the User can restrict or block the automatic downloading of cookies, however, this could
            reduce or even hinder certain elements of the functionality of the Website. Likewise, the User can choose
            the category of cookies that he/she wishes to activate in the cookies banner that appears the first time
            he/she accesses the Website.
          </CustomTypography>
        </Styled.BoxContent>

        <Styled.BoxContent>
          <CustomTypography variant="title-large" fontWeight={500}>
            Definition of cookies
          </CustomTypography>
          <CustomTypography variant="body-medium">
            Cookies are small text files that are placed on the User&apos;s computer, smartphone or other device when
            accessing the Internet. This is done to improve the User&apos;s experience and for other purposes, such as
            recognizing Users when accessing the Site, ensuring the security of your account and delivering targeted
            advertising.
          </CustomTypography>
          <CustomTypography variant="body-medium">
            For more general information about cookies, please see the following{' '}
            <PolicyLink href="https://es.wikipedia.org/wiki/Cookie_(inform%C3%A1tica)">article</PolicyLink>.
          </CustomTypography>
        </Styled.BoxContent>

        <Styled.BoxContent>
          <CustomTypography variant="title-large" fontWeight={500}>
            How we use cookies
          </CustomTypography>
          <CustomTypography variant="body-medium">
            In summary, Cellect uses the cookies listed in Annex I of this Policy to track how the Website is used in
            order to optimize its operation.
          </CustomTypography>
        </Styled.BoxContent>

        <Styled.BoxContent>
          <CustomTypography variant="title-large" fontWeight={500}>
            What cookies we use
          </CustomTypography>
          <CustomTypography variant="body-medium">
            The Web Site uses both its own and third-party cookies:
          </CustomTypography>
          <ul>
            <li>
              <CustomTypography variant="body-medium">
                <span className="body-medium weight-medium">First-party cookies:</span> cookies sent to your device by
                Cellect through the web domain.
              </CustomTypography>
            </li>
            <li>
              <CustomTypography variant="body-medium">
                <span className="body-medium weight-medium">Third-party cookies:</span> these are sent to your device by
                domains that are not managed by Cellect but by another entity that processes the data collected through
                cookies.
              </CustomTypography>
            </li>
          </ul>
          <CustomTypography variant="body-medium">
            According to the purpose of the cookies, the cookies used by Cellect can be divided into the following
            categories:
          </CustomTypography>
          <ul>
            <li>
              <CustomTypography variant="body-medium">
                <span className="body-medium weight-medium">Technical cookies (necessary):</span> cookies necessary for
                navigation and for the proper functioning of the Website. Their use allows basic functions, such as
                access, secure navigation. The legal basis that allows the collection of data through these cookies is
                the legitimate interest of Cellect in the management of the Website. No information collected through
                these cookies is shared with third parties. See the cookie table below for details of these cookies.
              </CustomTypography>
            </li>
            <li>
              <CustomTypography variant="body-medium">
                <span className="body-medium weight-medium">Analytical Cookies:</span> allow monitoring and analyzing
                the behavior of Users. The information collected through this type of cookies is used to measure the
                activity of the Website and for the elaboration of browsing profiles of the Users, in order to improve
                the Website and its services. The legal basis for collecting this data through these cookies is the
                consent of the User. See the table of cookies below for details of these cookies.
              </CustomTypography>
            </li>
            <li>
              <CustomTypography variant="body-medium">
                <span className="body-medium weight-medium">Preference cookies:</span> these are cookies that are
                installed in order to customize the User&apos;s experience (that is, the way in which certain windows
                will appear or not in the User&apos;s navigation) according to the consents and preferences that, where
                appropriate, the User expresses while browsing the Website, such as control over the acceptance of this
                Cookies Policy by the User (this will mean that the cookie warning banner will no longer appear, with
                its corresponding acceptance or not, for the User). As in the case of analytical cookies, the legitimate
                basis that allows us to collect such data from the User through these preference cookies is the consent
                of the User.
              </CustomTypography>
            </li>
            <li>
              <CustomTypography variant="body-medium">
                <span className="body-medium weight-medium">Advertising Cookies:</span> allow the management of
                advertising space based on different criteria. Those cookies that track the browsing habits of Users to
                obtain a profile that allows to offer the most interesting information or adjusted to their tastes and
                preferences identified on the basis of queries and browsing habits of the User, even on web pages not
                managed by Cellect. The legitimate basis for collecting this data through these cookies is the consent
                of the User. See the table of cookies below for details of these cookies.
              </CustomTypography>
            </li>
          </ul>
          <CustomTypography variant="body-medium">
            You can find the detailed list of the cookies we use, the type, purpose and duration of each one of them in
            the table of cookies below (Annex I).
          </CustomTypography>
        </Styled.BoxContent>

        <Styled.BoxContent>
          <CustomTypography variant="title-large" fontWeight={500}>
            Disable cookies
          </CustomTypography>
          <CustomTypography variant="body-medium">
            The User may restrict or limit the placement of cookies by adjusting the settings of your browser. However,
            if you select this setting, you may not be able to access certain parts of the Website or the poor display
            of its contents, which will result in less efficient browsing and you may not be able to take advantage of
            some of the services available. Therefore, it is recommended that you do not disable them.
          </CustomTypography>
          <CustomTypography variant="body-medium">
            If you wish to disable cookies, go to the Browser Preferences menu or Browser settings and look for the
            Privacy section. As an example, you can follow the instructions below:
          </CustomTypography>
          <ul>
            <li>
              <CustomTypography variant="body-medium">
                <PolicyLink href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09">
                  Microsoft Edge
                </PolicyLink>
              </CustomTypography>
            </li>
            <li>
              <CustomTypography variant="body-medium">
                <PolicyLink href="https://support.google.com/chrome/answer/95647">Google Chrome</PolicyLink>
              </CustomTypography>
            </li>
            <li>
              <CustomTypography variant="body-medium">
                <PolicyLink href="http://support.mozilla.org/kb/delete-cookies-remove-info-websites-stored">
                  Firefox
                </PolicyLink>
              </CustomTypography>
            </li>
            <li>
              <CustomTypography variant="body-medium">
                <PolicyLink href="https://support.apple.com/es-es/guide/safari/manage-cookies-and-website-data-sfri11471/mac">
                  Safari
                </PolicyLink>
              </CustomTypography>
            </li>
          </ul>
        </Styled.BoxContent>

        <Styled.BoxContent>
          <CustomTypography variant="title-large" fontWeight={500}>
            More information
          </CustomTypography>
          <CustomTypography variant="body-medium">
            We hope we have clarified how and why we use cookies. If anything is still unclear, please do not hesitate
            to contact us at: <PolicyLink href="mailto:info@cellectenergy.com">info@cellectenergy.com</PolicyLink>
          </CustomTypography>
          <CustomTypography variant="body-medium">Latest version: April 2026</CustomTypography>
        </Styled.BoxContent>

        <Styled.BoxContent>
          <CustomTypography variant="title-large" fontWeight={500}>
            Annex I
          </CustomTypography>
          <CustomTypography variant="body-medium">List of cookies</CustomTypography>
          <div style={{ width: '100%', overflow: 'auto' }}>
            <TableContainer sx={{ maxWidth: '100%' }}>
              <Table sx={{ minWidth: 900 }} aria-label="list of cookies">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableCellSx}>
                      <CustomTypography variant="body-large" fontWeight={500}>
                        Cookie
                      </CustomTypography>
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <CustomTypography variant="body-large" fontWeight={500}>
                        Cookie Type
                      </CustomTypography>
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <CustomTypography variant="body-large" fontWeight={500}>
                        Ownership
                      </CustomTypography>
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <CustomTypography variant="body-large" fontWeight={500}>
                        Definition
                      </CustomTypography>
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <CustomTypography variant="body-large" fontWeight={500}>
                        Conservation period
                      </CustomTypography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cookiesRows.map((cookie) => (
                    <TableRow key={cookie.cookie}>
                      <TableCell component="th" scope="row" sx={tableCellSx}>
                        <CustomTypography variant="body-medium">{cookie.cookie}</CustomTypography>
                      </TableCell>
                      <TableCell sx={tableCellSx}>
                        <CustomTypography variant="body-medium">{cookie.cookieType}</CustomTypography>
                      </TableCell>
                      <TableCell sx={tableCellSx}>
                        <CustomTypography variant="body-medium">{cookie.ownership}</CustomTypography>
                      </TableCell>
                      <TableCell sx={tableCellSx}>
                        <CustomTypography variant="body-medium">{cookie.definition}</CustomTypography>
                      </TableCell>
                      <TableCell sx={tableCellSx}>
                        <CustomTypography variant="body-medium">{cookie.conservationPeriod}</CustomTypography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Styled.BoxContent>
      </Styled.LegalContainer>
    </FullWidthLayout>
  )
}
